import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import Select, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import IndicatorsContainer from './components/IndicatorsContainer';
import OptionLabel from './components/OptionLabel';

function CustomSingleValue(props) {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div className="select-customizable__item-wrapper d-flex align-items-center justify-content-between w-100">
        <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{data.label}</div>
        {data.componentRight && <>{data.componentRight}</>}
      </div>
    </components.SingleValue>
  );
}
CustomSingleValue.propTypes = {
  data: PropTypes.shape({
    label: PropTypes.string,
    componentRight: PropTypes.node,
  }).isRequired,
};

function CustomOption(props) {
  const { children, data } = props;
  return (
    <components.Option {...props}>
      <div className="select-customizable__item-wrapper d-flex align-items-center justify-content-between w-100">
        <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</div>
        {data.componentRight && <>{data.componentRight}</>}
      </div>
    </components.Option>
  );
}
CustomOption.propTypes = {
  children: PropTypes.node,
  data: PropTypes.shape({
    componentRight: PropTypes.node,
  }).isRequired,
};

const SelectCustomizable = ({
  t,
  canCreate,
  defaultValue,
  error,
  isLoading,
  isMulti = false,
  isSkeleton = false,
  label,
  menuPosition = 'fixed',
  name,
  noOptionsMessage = 'No hay opciones...',
  onChange = () => null,
  onCreateOption,
  onKeyDown: onKeyDownProp,
  options = [],
  placeholder,
  placeholderCreateLabel,
  size = 'default',
  value,
  variant = 'default',
  ...props
}) => {
  // menuIsOpen, onMenuOpen and onMenuClose are controlled internally to support
  // keyboard navigation (Enter to open). External props with these names are ignored.
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Apply default values inside component to avoid "Cannot access before initialization" error
  const finalPlaceholder = placeholder || (t ? t('Select...') : 'Select...');
  const finalPlaceholderCreateLabel = placeholderCreateLabel || (t ? t('placeholder:Create new section') : 'Create new section');

  function handleKeyDown(event) {
    if (event.key === 'Enter' && !isMenuOpen) {
      event.preventDefault();
      setIsMenuOpen(true);
    }
    onKeyDownProp?.(event);
  }

  // Asociación programática error↔campo (WCAG 1.3.1): react-select solo propaga al input
  // interno aria-invalid y aria-errormessage (aria-describedby lo reserva para su placeholder),
  // y el span de error aporta el id referenciado.
  const autoId = React.useId();
  const errorId = `${name || autoId}-error`;
  const errorProps = error ? { 'aria-errormessage': errorId, 'aria-invalid': true } : {};

  function formatGroupLabel(data) {
    return (
      <div className="select-customizable__group-label">
        <span>{data.label}</span>
        <span className="select-customizable__group-badge">{data.options.length}</span>
      </div>
    );
  }

  function loadingMessage() {
    return (
      <div>
        <CircularProgress color="inherit" thickness={5} />
      </div>
    );
  }

  function getStyles() {
    return {
      // Fixes the overlapping problem of the component
      menu: (provided) => ({ ...provided, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      control: (provided, { isDisabled }) => ({ ...provided, cursor: isDisabled ? 'not-allowed' : 'pointer' }),
      option: (styles, { data }) => ({
        ...styles,
        fontFamily: data?.style?.fontFamily ? data.style.fontFamily : 'Inter',
        fontSize: 15,
      }),
    };
  }

  function getOptionLabel(option) {
    return option.label;
  }

  const customComponents = { SingleValue: CustomSingleValue, Option: CustomOption };

  return !isSkeleton ? (
    <div className="select-customizable-wrapper">
      {label && (
        <label className="select-customizable__label" title={typeof label === 'string' ? label : undefined}>
          {label} {props.required && <sup>*</sup>}
        </label>
      )}
      {canCreate ? (
        <CreatableSelect
          {...errorProps}
          className={`select-customizable select-customizable--${variant} select-customizable--size-${size}`}
          classNamePrefix="select-customizable"
          createOptionPosition="last"
          defaultValue={defaultValue}
          formatCreateLabel={(text) => `${finalPlaceholderCreateLabel}: ${text}`}
          isClearable={true}
          isLoading={isLoading}
          isMulti={isMulti}
          loadingMessage={loadingMessage}
          menuPlacement={'auto'}
          menuPosition={'fixed'}
          name={name}
          noOptionsMessage={() => noOptionsMessage}
          onChange={onChange}
          onCreateOption={onCreateOption}
          options={options}
          placeholder={finalPlaceholder}
          styles={getStyles()}
          value={value}
          components={{ ...props.components, ...customComponents }}
          {...props}
          menuIsOpen={isMenuOpen}
          onMenuOpen={() => setIsMenuOpen(true)}
          onMenuClose={() => setIsMenuOpen(false)}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <Select
          {...props}
          {...errorProps}
          className={`select-customizable select-customizable--${variant} select-customizable--size-${size} data-testid-${name}`}
          classNamePrefix="select-customizable"
          components={{ IndicatorsContainer, ...customComponents }}
          defaultValue={defaultValue}
          formatGroupLabel={formatGroupLabel}
          formatOptionLabel={OptionLabel}
          getOptionLabel={getOptionLabel}
          isLoading={isLoading}
          isMulti={isMulti}
          loadingMessage={loadingMessage}
          menuPlacement={'auto'}
          menuPosition={menuPosition}
          name={name}
          noOptionsMessage={() => noOptionsMessage}
          onChange={onChange}
          options={options}
          placeholder={finalPlaceholder}
          styles={getStyles()}
          value={value}
          menuIsOpen={isMenuOpen}
          onMenuOpen={() => setIsMenuOpen(true)}
          onMenuClose={() => setIsMenuOpen(false)}
          onKeyDown={handleKeyDown}
        />
      )}
      {error && (
        <span className="select-customizable__error" id={errorId}>
          {error}
        </span>
      )}
    </div>
  ) : (
    <div className="select-customizable-wrapper">
      {label && (
        <label className="select-customizable__label" title={typeof label === 'string' ? label : undefined}>
          {label} {props.required && <sup>*</sup>}
        </label>
      )}

      <Skeleton animation="wave" height={48} variant="text" />
    </div>
  );
};

SelectCustomizable.propTypes = {
  defaultValue: PropTypes.any,
  error: PropTypes.string,
  isLoading: PropTypes.bool,
  isMulti: PropTypes.bool,
  isSkeleton: PropTypes.bool,
  label: PropTypes.node,
  menuPosition: PropTypes.oneOf(['initial', 'fixed']),
  name: PropTypes.string,
  noOptionsMessage: PropTypes.string,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  options: PropTypes.array,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  size: PropTypes.oneOf(['default', 'small']),
  value: PropTypes.any,
  variant: PropTypes.oneOf(['default']),
  statusLabel: PropTypes.string,
  statusColor: PropTypes.string,
};

export default withTranslation(['placeholder'])(SelectCustomizable);
